// Drain the outbox to POST /api/v1/transactions/sync (spec §8.4).
//
// Per-item independent: one bad item never blocks the others. The server is
// idempotent by tx_uuid, so re-draining is always safe. A network failure
// reverts in-flight items to `pending` so the next trigger retries; per-item
// server verdicts mark each row `synced` (with its official invoice number) or
// `conflict` (with a reason) — never silently dropped.

import type { CommitPayload, OutboxItem } from "./db";
import { patchItem, pendingForDrain } from "./outbox";

export interface SyncResultItem {
  tx_uuid: string;
  status: "committed" | "rejected";
  invoice_number?: string;
  reconciliation_flag?: string;
  reason?: string;
  idempotent?: boolean;
}

/** Posts a batch of commits to the server and returns the per-item verdicts.
 *  Injected so the drain logic is testable without a real network. */
export type SyncPoster = (commits: CommitPayload[]) => Promise<{ results: SyncResultItem[] }>;

export interface DrainSummary {
  attempted: number;
  committed: number;
  conflicts: number;
}

export async function drainOutbox(poster: SyncPoster): Promise<DrainSummary> {
  const items: OutboxItem[] = await pendingForDrain();
  if (items.length === 0) {
    return { attempted: 0, committed: 0, conflicts: 0 };
  }

  for (const it of items) {
    await patchItem(it.tx_uuid, { status: "syncing" });
  }

  let response: { results: SyncResultItem[] };
  try {
    response = await poster(items.map((i) => i.payload));
  } catch (err) {
    // Transient failure: revert so a later trigger retries this batch.
    for (const it of items) {
      await patchItem(it.tx_uuid, { status: "pending" });
    }
    throw err;
  }

  const byId = new Map(response.results.map((r) => [r.tx_uuid, r]));
  let committed = 0;
  let conflicts = 0;
  for (const it of items) {
    const r = byId.get(it.tx_uuid);
    if (!r) {
      // Server didn't ack this item — leave it for the next drain.
      await patchItem(it.tx_uuid, { status: "pending" });
      continue;
    }
    if (r.status === "committed") {
      await patchItem(it.tx_uuid, {
        status: "synced",
        invoice_number: r.invoice_number,
        reconciliation_flag: r.reconciliation_flag,
        reason: undefined,
      });
      committed += 1;
    } else {
      await patchItem(it.tx_uuid, { status: "conflict", reason: r.reason });
      conflicts += 1;
    }
  }
  return { attempted: items.length, committed, conflicts };
}
